import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { format } from 'date-fns';
import { api } from '../lib/api';
import type {
  ApiStaff,
  ApiJob,
  ApiBoardLeaveEntry,
  ApiBoardSegment,
  ApiBoardVehicleAssignment,
  ApiBoardPostponement,
} from '../lib/api';

export interface MergedBoardData {
  staff: ApiStaff[];
  // Phase 39-05: per-(phase, date) job rows merged across the loaded weeks.
  // Deduped by phase_id (board endpoint returns one row per phase, not per
  // phase-date — date-membership is derived from start_date/end_date in
  // the layout pipeline).
  jobs: ApiJob[];
  // Phase 39-05 follow-up (2026-05-25, Migration 065): self-contained
  // postponement snapshots. Deduped by snapshot id (each row is unique).
  postponements: ApiBoardPostponement[];
  leave: ApiBoardLeaveEntry[];
  segments: ApiBoardSegment[];
  vehicles: ApiBoardVehicleAssignment[];
}

/**
 * Fetches board availability data for multiple weeks around a center date.
 *
 * Returns staff (deduplicated), leave, and segments across all loaded weeks.
 * Jobs come from useJobs() separately — this hook only provides availability.
 *
 * Each week is fetched independently via GET /api/v1/board/week?date=...
 * React Query deduplication ensures revisited weeks use cache.
 */
export function useBoardWeeks(weekStarts: Date[]) {
  const queries = useQueries({
    queries: weekStarts.map((ws) => {
      const dateStr = format(ws, 'yyyy-MM-dd');
      return {
        queryKey: ['board', 'week', dateStr],
        queryFn: () => api.board.week(dateStr),
        staleTime: 30 * 1000,
        // PERF-1: poll every 5 min (was 30s) and stop polling when the tab is
        // backgrounded. TanStack still refetches on window focus by default,
        // so returning to the tab brings data current immediately.
        refetchInterval: 5 * 60 * 1000,
        refetchIntervalInBackground: false,
      };
    }),
  });

  // Stable fingerprint for memo — queries array is a new reference every render
  const queryFingerprint = queries.map((q) => `${q.dataUpdatedAt}-${q.status}`).join(',');

  const merged = useMemo<MergedBoardData>(() => {
    const staffMap = new Map<string, ApiStaff>();
    const leaveMap = new Map<string, ApiBoardLeaveEntry>();
    const segmentKeys = new Set<string>();
    const allSegments: ApiBoardSegment[] = [];
    // Phase 37-01 (Issue #51): dedup vehicles by (job_id, date) composite key;
    // weeks overlap at boundaries so the same (job, date) may appear twice.
    const vehiclesKeys = new Set<string>();
    const allVehicles: ApiBoardVehicleAssignment[] = [];
    // Phase 39-05: dedup phase rows by phase_id (board endpoint returns one
    // row per phase across the entire window; the same phase appears in
    // multiple loaded weeks when its [start_date, end_date] spans week
    // boundaries).
    const jobsMap = new Map<string, ApiJob>();
    // Phase 39-05 follow-up (2026-05-25): dedup postponement snapshots by
    // their own row id (unique per phase preserved per postpone event).
    const postponementsMap = new Map<string, ApiBoardPostponement>();

    for (const q of queries) {
      if (!q.data) continue;
      // Deduplicate staff — same for all weeks
      for (const s of q.data.staff) {
        if (!staffMap.has(s.id)) staffMap.set(s.id, s);
      }
      // Deduplicate leave by id
      for (const l of q.data.leave) {
        if (!leaveMap.has(l.id)) leaveMap.set(l.id, l);
      }
      // Deduplicate segments by id:date composite key
      for (const seg of q.data.segments) {
        const key = `${seg.id}:${seg.date}`;
        if (!segmentKeys.has(key)) {
          segmentKeys.add(key);
          allSegments.push(seg);
        }
      }
      // Phase 39-05: dedup vehicle assignments by (phase_id, date) so
      // multi-phase jobs with vehicles on different phases the same day
      // don't collapse into a single row. Pre-39-05 keyed by job_id which
      // would have lost per-phase distinctions.
      for (const v of q.data.vehicles ?? []) {
        const key = `${v.phase_id}:${v.date}`;
        if (!vehiclesKeys.has(key)) {
          vehiclesKeys.add(key);
          allVehicles.push(v);
        }
      }
      // Phase 39-05: dedup phase rows. Key on phase_id (always present in
      // 39-04+ projection); fall back to job_id for any legacy row missing
      // phase_id so existing-data resilience is preserved.
      for (const j of q.data.jobs ?? []) {
        const key = j.phase_id ?? j.id;
        if (!jobsMap.has(key)) jobsMap.set(key, j);
      }
      // Phase 39-05 follow-up (Migration 065): postponement snapshots.
      for (const p of q.data.postponements ?? []) {
        if (!postponementsMap.has(p.id)) postponementsMap.set(p.id, p);
      }
    }

    return {
      staff: Array.from(staffMap.values()),
      jobs: Array.from(jobsMap.values()),
      postponements: Array.from(postponementsMap.values()),
      leave: Array.from(leaveMap.values()),
      segments: allSegments,
      vehicles: allVehicles,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFingerprint]);

  const isLoading = queries.some((q) => q.isLoading);
  // #218 v2: per-week loading so a slide into an unfetched week only pulses THAT
  // week's columns (not all). Keyed by the same yyyy-MM-dd week-start used for the
  // queryKey. Computed inline (small loop; the Set is never a child prop, so a memo
  // buys nothing and would miss a fetchStatus-only change).
  const loadingWeeks = new Set<string>();
  queries.forEach((q, i) => {
    if (q.isLoading && weekStarts[i]) loadingWeeks.add(format(weekStarts[i], 'yyyy-MM-dd'));
  });
  const hasPartialError = queries.some((q) => q.isError) && !queries.every((q) => q.isError);
  const dataUpdatedAt = Math.max(0, ...queries.map((q) => q.dataUpdatedAt ?? 0));

  return {
    data: merged,
    isLoading,
    loadingWeeks,
    hasPartialError,
    dataUpdatedAt,
    weekStarts,
  };
}
